import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, RadioGroup } from 'react-form';
import moment from 'moment-timezone';
import PropTypes, { any } from 'prop-types';

import styles from './nextAvailable.scss';
import { CheckBox, RadioButton } from '../../../components/Form';
import {
    allCourtsCheckboxKey,
    dateFormat,
    dateFormatForDayConfig,
    getDefaultFacilitiesSelected,
    getSelectedCourtIds,
    hasInValidCourtSelection,
} from '../../../utils/courtSchedular';
import { getApiAxiosInstance } from '../../../utils/network';
import NextAvailableSidePanel from './NextAvailableSidePanel';
import { getDefaultValuesFromConfigObject, MatchStartOptions, MatchStartOptionsList, ScoreSheetPrintOptions, ScoreSheetPrintOptionsList } from '../../../utils/nextAvailable';
import { saveNextAvailableConfigData } from '../../../actions/next-available-actions';
import { getNextAvailableConfigForTheDay } from '../../../api/tmsEventsNextAvailableApi';
import ConfirmationPopup from './ConfirmationPopup';

const NextAvailableConfig = (props) => {
    const {
        className,
        isEdit,
        closeEditCourtDeskConfig,
        activeFacilitiesAndCourts,
        eventId,
        eventSchedule,
    } = props;

    const dispatch = useDispatch();
    const nextAvailable = useSelector(({ nextAvailable }) => nextAvailable);

    const { selectedDate, defaultCourtConfig } = nextAvailable;
    const [currentDateConfig, setCurrentDateConfig] = useState(null);
    const [confirmCourtChangeData, setConfirmCourtChangeData] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
    };

    const dateOfConfig = moment(selectedDate).tz(eventSchedule?.timeZone?.value).format(dateFormat);

    const defaultValues = useMemo(() => {
        if (!isEdit) {
            if (defaultCourtConfig) {
                return getDefaultValuesFromConfigObject(
                    defaultCourtConfig,
                    eventSchedule,
                    activeFacilitiesAndCourts,
                    selectedDate,
                );
            }

            return {
                date: selectedDate,
                printScoreSheetType: ScoreSheetPrintOptions.PLACED_ON_COURT,
                matchStartType: MatchStartOptions.PLACED_ON_COURT,
                printCompletedScoreSheet: false,
                printMedalReport: false,
                facilities: getDefaultFacilitiesSelected(activeFacilitiesAndCourts),
            };
        }

        if (currentDateConfig) {
            return getDefaultValuesFromConfigObject(
                currentDateConfig,
                eventSchedule,
                activeFacilitiesAndCourts,
                selectedDate,
            );
        }

        return null;
    }, [selectedDate, currentDateConfig, defaultCourtConfig, activeFacilitiesAndCourts, eventSchedule]);

    useEffect(() => {
        const getDayConfig = async () => {
            const response = await getNextAvailableConfigForTheDay(getApiAxiosInstance(), eventId, {
                date: moment.utc(selectedDate).format(dateFormatForDayConfig),
            });
            setCurrentDateConfig(response.data);
        };

        if (isEdit && !currentDateConfig) {
            getDayConfig();
        }
    }, [isEdit, currentDateConfig, eventId, eventSchedule]);

    const submitButtonText = isEdit ? 'Save settings' : 'Set Courts';

    const onFormSubmit = async (values, checkCourtIdsChange = true) => {
        const updatedValues = {
            date: moment.utc(values.date).format(),
            courtIds: getSelectedCourtIds(values.facilities),
            printScoreSheetType: values.printScoreSheetType,
            matchStartType: values.matchStartType,
            printCompletedScoreSheet: values.printCompletedScoreSheet,
            printMedalReport: values.printMedalReport,
        };

        if (checkCourtIdsChange && currentDateConfig) {
            const allCourtsWithMatchesAreEnabled = currentDateConfig.courts.reduce((enabled, court) => {
                if (court.hasScheduledMatches) {
                    return enabled && updatedValues.courtIds.includes(court.courtId);
                }

                return enabled;
            }, true);

            if (!allCourtsWithMatchesAreEnabled) {
                setConfirmCourtChangeData(values);
                return;
            }
        }

        const shouldRefetchUnScheduleMatches = !checkCourtIdsChange;
        await dispatch(saveNextAvailableConfigData(eventId, updatedValues, shouldRefetchUnScheduleMatches));

        if (isEdit) {
            closeEditCourtDeskConfig();
        }
    };

    const onCloseFormSubmissionConfirm = () => {
        setConfirmCourtChangeData(null);
    };

    const onAllCourtsCheckChange = (formApi, facility) => (value) => {
        facility.courts.forEach((court) => {
            formApi.setValue(['facilities', facility.id, 'courts', court.id, 'selected'], value);
        });
    };

    const onCourtCheckChange = (formApi, facility, court) => (value) => {
        const selectedCourts = facility.courts.filter((courtObj) => {
            if (courtObj.id === court.id) {
                return value;
            }

            return formApi.getValue(['facilities', facility.id, 'courts', courtObj.id, 'selected']);
        });

        formApi.setValue(['facilities', facility.id, 'courts', allCourtsCheckboxKey, 'selected'], selectedCourts.length === facility.courts.length);
    };

    if (!defaultValues) {
        return null;
    }

    return (
        <>
            <Form
                onSubmit={onFormSubmit}
                defaultValues={defaultValues}
            >
                {(formApi) => {
                    const { submitForm, getValue, errors, values } = formApi;

                    return (
                        <form onSubmit={submitForm} id="dayConfigForm">
                            <NextAvailableSidePanel
                                className={className}
                                closeSideBar={handleClose}
                                isVisible={isVisible}
                                onClose={closeEditCourtDeskConfig}
                            >
                                <div className={styles.header} data-testid="next-available.config.title">
                                    {`Settings for ${dateOfConfig}`}
                                </div>
                                <div className={styles.content}>
                                    <div className={styles.section}>
                                        <h6 className={styles.sectionTitle} data-testid="next-available.config.printing-title">
                                            Printing
                                        </h6>
                                        <div className={styles.sectionDescription} data-testid="next-available.config.printing-description">
                                            Print score sheets when:
                                        </div>
                                        <RadioGroup
                                            field="printScoreSheetType"
                                        >
                                            {ScoreSheetPrintOptionsList.map(({ id, label }) => (
                                                <RadioButton
                                                    key={`printScoreSheetType-${id}`}
                                                    id={`printScoreSheetType-${id}`}
                                                    label={label}
                                                    value={id}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </div>
                                    <div className={styles.section}>
                                        <h6 className={styles.sectionTitle} data-testid="next-available.config.additional-printing-title">
                                            Additional printing options
                                        </h6>
                                        <CheckBox
                                            id="printCompletedScoreSheet"
                                            field="printCompletedScoreSheet"
                                            customLabel="Print completed score sheet after scores are posted"
                                            formGroupCssClass={`${styles.checkbox}`}
                                            additionalClassName={`${styles.customCheckboxOptionStyle}`}
                                            iconClassName={`${styles.customCheckboxIconStyle}`}
                                        />
                                        <CheckBox
                                            id="printMedalReport"
                                            field="printMedalReport"
                                            customLabel="Auto print medal report following completion of all matches."
                                            formGroupCssClass={`${styles.checkbox}`}
                                            additionalClassName={`${styles.customCheckboxOptionStyle}`}
                                            iconClassName={`${styles.customCheckboxIconStyle}`}
                                        />
                                    </div>
                                    <div className={styles.section}>
                                        <h6 className={styles.sectionTitle} data-testid="next-available.config.start-matches-title">
                                            Start matches
                                        </h6>
                                        <div className={styles.sectionDescription} data-testid="next-available.config.start-matches-description">
                                            How would you like to start matches? Note: On match start players will receive an SMS.
                                        </div>
                                        <RadioGroup
                                            field="matchStartType"
                                        >
                                            {MatchStartOptionsList.map(({ id, label }) => (
                                                <RadioButton
                                                    key={`matchStartType-${id}`}
                                                    id={`matchStartType-${id}`}
                                                    label={label}
                                                    value={id}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </div>
                                    <div className={styles.section} data-testid="next-available.config.facility-section">
                                        <h6 className={styles.sectionTitle} data-testid="next-available.config.facility-title">
                                            Facility
                                        </h6>
                                        {activeFacilitiesAndCourts.length > 1 && (
                                            <>
                                                <p className={styles.sectionDescription} data-testid="next-available.config.facility-multiple-description">
                                                    Please select a facility and courts to create your court schedule grid
                                                </p>
                                                {activeFacilitiesAndCourts.map((facility) => {
                                                    return (
                                                        <div
                                                            key={`facility-${facility.id}`}
                                                            className={styles.facility}
                                                        >
                                                            <CheckBox
                                                                id={`facilities${facility.id}selected`}
                                                                field={['facilities', facility.id, 'selected']}
                                                                customLabel={facility.name}
                                                                formGroupCssClass={styles.checkbox}
                                                            />
                                                            {getValue(['facilities', facility.id, 'selected']) && (
                                                                <>
                                                                    {facility.courts.length > 1 && (
                                                                        <CheckBox
                                                                            id={`facilities${facility.id}${allCourtsCheckboxKey}selected`}
                                                                            field={['facilities', facility.id, 'courts', allCourtsCheckboxKey, 'selected']}
                                                                            customLabel="All Courts"
                                                                            formGroupCssClass={`${styles.checkbox} ml32`}
                                                                            onChange={onAllCourtsCheckChange(formApi, facility)}
                                                                        />
                                                                    )}
                                                                    {facility.courts.map((court) => {
                                                                        return (
                                                                            <CheckBox
                                                                                key={`facility-${facility.id}-court-${court.id}`}
                                                                                id={`facilities${facility.id}${court.id}`}
                                                                                field={['facilities', facility.id, 'courts', court.id, 'selected']}
                                                                                customLabel={court.name}
                                                                                formGroupCssClass={`${styles.checkbox} ml32`}
                                                                                onChange={onCourtCheckChange(formApi, facility, court)}
                                                                            />
                                                                        );
                                                                    })}
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                        {activeFacilitiesAndCourts.length === 1 && (
                                            <>
                                                <p className={styles.sectionDescription} data-testid="next-available.config.facility-single-description">
                                                    Which court(s) would you like to use?
                                                </p>
                                                <h6 className={styles.sectionTitle} data-testid="next-available.config.facility-name">
                                                    {activeFacilitiesAndCourts[0].name}
                                                </h6>
                                                <div
                                                    className={styles.facility}
                                                >
                                                    {activeFacilitiesAndCourts[0].courts?.length > 1 && (
                                                        <CheckBox
                                                            id={`facilities${activeFacilitiesAndCourts[0].id}${allCourtsCheckboxKey}selected`}
                                                            field={['facilities', activeFacilitiesAndCourts[0].id, 'courts', allCourtsCheckboxKey, 'selected']}
                                                            customLabel="All Courts"
                                                            formGroupCssClass={`${styles.checkbox}`}
                                                            onChange={onAllCourtsCheckChange(formApi, activeFacilitiesAndCourts[0])}
                                                        />
                                                    )}
                                                    {activeFacilitiesAndCourts[0].courts?.map((court) => {
                                                        return (
                                                            <CheckBox
                                                                key={`facility-${activeFacilitiesAndCourts[0].id}-court-${court.id}`}
                                                                id={`facilities${activeFacilitiesAndCourts[0].id}${court.id}`}
                                                                field={['facilities', activeFacilitiesAndCourts[0].id, 'courts', court.id, 'selected']}
                                                                customLabel={court.name}
                                                                formGroupCssClass={`${styles.checkbox}`}
                                                                onChange={onCourtCheckChange(formApi, activeFacilitiesAndCourts[0], court)}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className={`${styles.footer} d-flex`}>
                                    <button
                                        className={`btn btn-default ${styles.submitButton} flex-1 mr8`}
                                        type="button"
                                        onClick={handleClose}
                                        data-testid="next-available.config.cancel-btn"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className={`btn btn-primary ${styles.submitButton} flex-1`}
                                        type="submit"
                                        disabled={nextAvailable.isSavingConfig || errors || hasInValidCourtSelection(values)}
                                        data-testid="next-available.config.submit-btn"
                                    >
                                        {nextAvailable.isSavingConfig && (<i className="fas fa-circle-notch fa-spin" />)} {submitButtonText}
                                    </button>
                                </div>
                            </NextAvailableSidePanel>
                        </form>
                    );
                }}
            </Form>
            {confirmCourtChangeData && (
                <ConfirmationPopup
                    description="Some matches are scheduled on the court you are unselecting for this day. You can not unselect these courts without rescheduling the matches."
                    confirmText="OK"
                    onConfirm={onCloseFormSubmissionConfirm}
                    onCancel={onCloseFormSubmissionConfirm}
                    data-testid="modal.next-available.confirm-court-change"
                />
            )}
        </>
    );
};

NextAvailableConfig.propTypes = {
    className: PropTypes.string,
    isEdit: PropTypes.bool,
    closeEditCourtDeskConfig: PropTypes.func,
    activeFacilitiesAndCourts: PropTypes.arrayOf(any).isRequired,
    eventId: PropTypes.number.isRequired,
    eventSchedule: PropTypes.objectOf(any).isRequired,
};

NextAvailableConfig.defaultProps = {
    className: '',
    isEdit: false,
    closeEditCourtDeskConfig: () => {},
};

export default NextAvailableConfig;
